import { Head } from '@inertiajs/react';
import Layout from '@/layouts/Layout';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { WelcomeHeader } from '@/components/dashboard/WelcomeHeader';
import { DailyActionsCard } from '@/components/dashboard/DailyActionsCard';
import { StreakCounter } from '@/components/dashboard/StreakCounter';
import { RecentActivity } from '@/components/dashboard/RecentActivity';
import { QuickStats } from '@/components/dashboard/QuickStats';
import { ResourcesCard } from '@/components/dashboard/ResourcesCard';

export interface DashboardData {
  patient: {
    id: number;
    name: string;
    first_name: string;
    last_seen_at?: string;
  };
  streak: {
    current: number;
    longest: number;
    last_completed_date?: string;
  };
  today_journal: {
    status: 'completed' | 'in_progress' | 'not_started';
    id?: number;
    chat_id?: number;
  };
  recent_activity: Array<{
    type: 'journal' | 'chat' | 'login';
    description: string;
    timestamp: string;
    link?: string;
  }>;
  stats: {
    journals_count: number;
    chats_count: number;
    days_in_therapy: number;
    completion_rate: number;
  };
  motivational_quote: string;
}

interface Props {
  patient: DashboardData['patient'];
  streak: DashboardData['streak'];
  today_journal: DashboardData['today_journal'];
  recent_activity: DashboardData['recent_activity'];
  stats: DashboardData['stats'];
  motivational_quote: string;
}

export default function Dashboard({
  patient,
  streak,
  today_journal,
  recent_activity,
  stats,
  motivational_quote,
}: Props) {
  const mainHeader = (
    <div className="w-full bg-gradient-to-b from-primary/20 flex flex-col items-center mb-4">
      <div className="w-full">
        <WelcomeHeader
          name={patient.first_name}
          lastSeenAt={patient.last_seen_at}
          motivationalQuote={motivational_quote}
        />
      </div>
    </div>
  );

  return (
    <Layout user={{ name: patient.name }} mainHeader={mainHeader}>
      <Head title="Dashboard" />
      <DashboardLayout>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Primary CTA Card - Full width on mobile, spans 2 cols on larger screens */}
          <div className="md:col-span-2 lg:col-span-2">
            <DailyActionsCard todayJournal={today_journal} />
          </div>

          {/* Streak Counter */}
          <div className="lg:col-span-1">
            <StreakCounter streak={streak} />
          </div>

          {/* Recent Activity */}
          <div className="md:col-span-1 lg:col-span-2">
            <RecentActivity activities={recent_activity} />
          </div>

          {/* Quick Stats */}
          <div className="md:col-span-1 lg:col-span-1">
            <QuickStats stats={stats} />
          </div>

          {/* Resources - Full width */}
          <div className="md:col-span-2 lg:col-span-3">
            <ResourcesCard />
          </div>
        </div>
      </DashboardLayout>
    </Layout>
  );
}
